/**
 * Cookie Manager Utility
 * Handles plaintext cookie operations following WordPress Header.php pattern
 * - Domain: now.com (cross-subdomain)
 * - Path: / (site-wide)
 * - Expiry: 30 days (2592000 seconds)
 * - Encryption: Handled by Header.php server-side only
 */

interface CookieOptions {
  days?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Set a cookie with default options matching WordPress Header.php pattern
 * @param name - Cookie name
 * @param value - Cookie value (plaintext)
 * @param options - Optional cookie options
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): void => {
  const {
    days = 30,
    domain = 'now.com',
    path = '/',
    secure = false,
    sameSite = 'None'
  } = options;

  // Calculate expiration date
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + days * 24 * 60 * 60 * 1000);

  // Build cookie string
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookieString += `; expires=${expirationDate.toUTCString()}`;
  cookieString += `; path=${path}`;
  cookieString += `; domain=${domain}`;
  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }
  if (secure) {
    cookieString += '; Secure';
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
};

/**
 * Delete a cookie by setting expiration to past
 * Mirrors WordPress pattern: setcookie('name', '', time() - 3600, "/", "now.com")
 * @param name - Cookie name
 * @param domain - Cookie domain (default: now.com)
 */
export const deleteCookie = (name: string, domain: string = 'now.com'): void => {
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() - 3600 * 1000); // 1 hour in past

  let cookieString = `${encodeURIComponent(name)}=`;
  cookieString += `; expires=${expirationDate.toUTCString()}`;
  cookieString += `; path=/`;
  cookieString += `; domain=${domain}`;

  document.cookie = cookieString;
};

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns true if cookie exists, false otherwise
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
};
